import { useCallback } from "react";
import { useMsal } from "@azure/msal-react";
import { InteractionRequiredAuthError } from "@azure/msal-browser";
import { tokenRequest } from "./msalConfig";

/**
 * Returns a function that acquires an access token for the configured API scope. Tries silent
 * first (uses the cached token if still valid, otherwise refreshes via hidden iframe); falls back
 * to redirect on `InteractionRequiredAuthError`. Returns null if no account is signed in.
 */
export function useAccessToken(): () => Promise<string | null> {
  const { instance, accounts } = useMsal();
  return useCallback(async () => {
    const account = accounts[0] ?? instance.getActiveAccount() ?? null;
    if (!account) return null;
    try {
      const result = await instance.acquireTokenSilent({ ...tokenRequest, account });
      return result.accessToken;
    } catch (err) {
      if (err instanceof InteractionRequiredAuthError) {
        await instance.acquireTokenRedirect({ ...tokenRequest, account });
      }
      // Either we kicked off a redirect (above) or something else broke — return null and let
      // the calling fetch surface a 401 the screen can render.
      return null;
    }
  }, [instance, accounts]);
}
